import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { normalizeLeadRecord } from "./utils";
import type { Lead, LeadChange } from "./types";

function normalizeLeads(value: unknown): Lead[] {
  return Array.isArray(value)
    ? value.map((lead) => normalizeLeadRecord(lead as Partial<Lead>))
    : [];
}

function sortLeads(leads: Lead[]): Lead[] {
  return [...leads].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

function mergeLead(current: Lead[], value: Partial<Lead>): Lead[] {
  const incoming = normalizeLeadRecord(value);
  if (incoming.deleted_at)
    return current.filter((lead) => lead.id !== incoming.id);
  return sortLeads([
    incoming,
    ...current.filter((lead) => lead.id !== incoming.id),
  ]);
}

export function useLeads(
  enabled: boolean,
  onSessionRevoked: (reason: "kick" | "ban") => void,
) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncState, setSyncState] = useState<
    "connecting" | "online" | "offline"
  >("connecting");

  useEffect(() => {
    if (!enabled) {
      setLeads([]);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setSyncState("connecting");

    void api<{ leads: unknown[] }>("/api/leads")
      .then(({ leads: initial }) => {
        if (!active) return;
        setLeads(sortLeads(normalizeLeads(initial)));
        setSyncState("online");
      })
      .catch(() => {
        if (active) setSyncState("offline");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const events = new EventSource("/api/events");
    events.addEventListener("leads", (event) => {
      if (!active) return;
      const { lead } = JSON.parse((event as MessageEvent).data) as {
        lead: Partial<Lead>;
      };
      setLeads((current) => mergeLead(current, lead));
      setSyncState("online");
    });
    events.addEventListener("session", (event) => {
      if (!active) return;
      const { reason } = JSON.parse((event as MessageEvent).data) as {
        reason: "kick" | "ban";
      };
      onSessionRevoked(reason);
    });
    events.onopen = () => {
      if (active) setSyncState("online");
    };
    events.onerror = () => {
      if (active) setSyncState("offline");
    };

    return () => {
      active = false;
      events.close();
    };
  }, [enabled, onSessionRevoked]);

  const saveLead = useCallback(async (input: Lead) => {
    const { lead } = await api<{ lead: Lead }>(
      `/api/leads/${encodeURIComponent(input.id)}`,
      {
        method: "PUT",
        body: JSON.stringify(normalizeLeadRecord(input)),
      },
    );
    setLeads((current) => mergeLead(current, lead));
    return lead;
  }, []);

  const saveMany = useCallback(async (inputs: Lead[]) => {
    const result = await api<{ upserts: Lead[] }>("/api/leads/batch", {
      method: "POST",
      body: JSON.stringify({
        upserts: inputs.map((lead) => normalizeLeadRecord(lead)),
        deleteIds: [],
      }),
    });
    setLeads((current) => result.upserts.reduce(mergeLead, current));
    return result.upserts;
  }, []);

  const deleteLead = useCallback(async (id: string) => {
    await api<{ ok: true }>(`/api/leads/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    setLeads((current) => current.filter((lead) => lead.id !== id));
  }, []);

  const applyLeadChanges = useCallback(async (changes: LeadChange[]) => {
    const upserts = changes
      .map((change) => change.after)
      .filter((lead): lead is Lead => Boolean(lead));
    const deleteIds = changes
      .filter((change) => !change.after && change.before)
      .map((change) => change.before!.id);
    const result = await api<{ upserts: Lead[]; deleted: Lead[] }>(
      "/api/leads/batch",
      {
        method: "POST",
        body: JSON.stringify({ upserts, deleteIds }),
      },
    );
    setLeads((current) =>
      [...result.upserts, ...result.deleted].reduce(mergeLead, current),
    );
  }, []);

  return {
    leads,
    loading,
    syncState,
    saveLead,
    saveMany,
    deleteLead,
    applyLeadChanges,
  };
}
